/**
 * SitePick front — クリップボードへのコピー（共通。shared-foundation S9）。
 *
 * `navigator.clipboard.writeText()` を優先し、使えない（非 secure context / 権限拒否 / 未実装）ときは
 * 画面外の textarea + `document.execCommand("copy")` で試す。どちらも失敗したら false を返す
 * （呼び出し側が「リンクを選択状態にして手動でコピーしてもらう」等の代替を出す）。例外は投げない。
 */

/**
 * textarea + execCommand のフォールバック。
 *
 * @param {string} text
 * @returns {boolean}
 */
function legacyCopy(text) {
  if (typeof document === "undefined" || !document.body || typeof document.execCommand !== "function") {
    return false;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.setAttribute("aria-hidden", "true");
  // 画面外に置く（スクロール位置を動かさない・iOS のズームを起こさない 16px）。
  area.style.position = "fixed";
  area.style.top = "0";
  area.style.left = "-9999px";
  area.style.fontSize = "16px";
  document.body.appendChild(area);
  const active = document.activeElement;
  let ok = false;
  try {
    area.select();
    area.setSelectionRange(0, text.length);
    ok = document.execCommand("copy") === true;
  } catch {
    ok = false;
  } finally {
    area.remove();
    if (active && typeof active.focus === "function") {
      try {
        active.focus({ preventScroll: true });
      } catch {
        /* noop */
      }
    }
  }
  return ok;
}

/**
 * テキストをクリップボードへコピーする。
 *
 * @param {string} text
 * @returns {Promise<boolean>} 成功したら true、失敗したら false。
 */
export async function copyText(text) {
  const value = typeof text === "string" ? text : String(text ?? "");
  if (value === "") {
    return false;
  }
  try {
    const nav = typeof navigator !== "undefined" ? navigator : null;
    if (nav && nav.clipboard && typeof nav.clipboard.writeText === "function") {
      await nav.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* 権限拒否など → フォールバックへ */
  }
  return legacyCopy(value);
}
